// BakedStyleModal.tsx — selección de estilos del paso 'baked-style' a pantalla
// grande. Elegir un estilo es una decisión VISUAL: en el rail de 392px los
// thumbnails entraban de a tres y no se veía qué se estaba eligiendo.
// Qué exporta: BakedStyleModal.
// Quién lo usa: widgets/BakedStylePicker.tsx.

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BakedStyleOption } from '../types';
import {
  Overlay, Panel, Head, HeadText, HeadSearch, SearchInput, SearchIcon, CloseBtn,
  Body, GridPane, StyleGrid, StyleCard, StylePlaceholder, StyleName, PickBadge,
  CustomBadge, SidePane, SideTitle, OptionRow, PhotoGrid, PhotoCell, PhotoCheck,
  DimWhenIntact, EmptyGrid, Foot, FootCount, FootActions, GhostBtn, PrimaryBtn,
  IndustryTag, IndustryFilters, IndustryChip, ChipCount,
} from '../styleModalStyled';
import { industryColors, industryKey, normalizeIndustry } from '../../../utils/industryTag';

interface Props {
  open: boolean;
  onClose: () => void;
  bakedStyles: BakedStyleOption[];
  selectedStyleIds: string[];
  toggleBakedStyle: (id: string) => void;
  useLogo: boolean;
  setUseLogo: (v: boolean) => void;
  myPhotos: { id: string; url: string; name?: string }[];
  selectedPhotoIds: string[];
  togglePhoto: (id: string) => void;
  confirmBakedStyles: () => void;
  autoFillGreenbox: boolean;
  setAutoFillGreenbox: (v: boolean) => void;
  anyGreenboxSelected: boolean;
  keepPhotoIntact: boolean;
  setKeepPhotoIntact: (v: boolean) => void;
}

const CheckGlyph = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function BakedStyleModal({
  open, onClose, bakedStyles, selectedStyleIds, toggleBakedStyle,
  useLogo, setUseLogo, myPhotos, selectedPhotoIds, togglePhoto,
  confirmBakedStyles, autoFillGreenbox, setAutoFillGreenbox, anyGreenboxSelected,
  keepPhotoIntact, setKeepPhotoIntact,
}: Props) {
  const [query, setQuery] = useState('');
  const [industryFilter, setIndustryFilter] = useState<string | null>(null);

  // Escape cierra. Y mientras está abierto bloqueamos el scroll del fondo: si
  // no, scrollear dentro de la grilla arrastra la página de atrás.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Rubros presentes, ordenados por cantidad. Solo los que existen: mostrar
  // rubros vacíos sería ruido.
  const industries = useMemo(() => {
    const map = new Map<string, { label: string; count: number }>();
    for (const s of bakedStyles) {
      const label = normalizeIndustry(s.industry);
      if (!label) continue;
      const key = industryKey(label);
      const prev = map.get(key);
      if (prev) prev.count++;
      else map.set(key, { label, count: 1 });
    }
    return [...map.entries()]
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [bakedStyles]);

  const untaggedCount = useMemo(
    () => bakedStyles.filter((s) => !normalizeIndustry(s.industry)).length,
    [bakedStyles]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bakedStyles.filter((s) => {
      if (q && !(s.label || '').toLowerCase().includes(q)) return false;
      if (industryFilter === null) return true;
      if (industryFilter === '__none__') return !normalizeIndustry(s.industry);
      return industryKey(s.industry) === industryFilter;
    });
  }, [bakedStyles, query, industryFilter]);

  if (!open) return null;

  // Con "keep as-is" la foto no se restyliza, así que los estilos son
  // irrelevantes: alcanza con ≥1 foto para continuar.
  const intactReady = keepPhotoIntact && selectedPhotoIds.length > 0;
  const canContinue = selectedStyleIds.length > 0 || intactReady;

  // Portal a <body>: el modal se monta dentro del rail, y GenerateBody tiene un
  // transform (la escena de generación lo hace retroceder). Un transform en un
  // ancestro convierte a position:fixed en absolute y crea un stacking context,
  // así que sin portal el modal quedaba atrapado: la barra de filtros y los
  // botones de las cards se pintaban ENCIMA.
  return createPortal(
    <Overlay onClick={onClose} role="dialog" aria-modal="true" aria-label="Choose poster styles">
      <Panel onClick={(e) => e.stopPropagation()}>
        <Head>
          <HeadText>
            <h2>Pick your poster styles</h2>
            <p>Every post comes back as full AI artwork, cycling through what you pick.</p>
          </HeadText>

          <HeadSearch>
            <SearchIcon>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="4.75" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </SearchIcon>
            <SearchInput
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search styles"
              aria-label="Search styles"
            />
          </HeadSearch>

          <CloseBtn type="button" onClick={onClose} aria-label="Close">
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </CloseBtn>
        </Head>

        <Body>
          <GridPane>
            {industries.length > 0 && (
              <IndustryFilters>
                <IndustryChip
                  type="button"
                  $active={industryFilter === null}
                  $bg="transparent" $fg="currentColor" $border="currentColor"
                  onClick={() => setIndustryFilter(null)}
                >
                  All <ChipCount>{bakedStyles.length}</ChipCount>
                </IndustryChip>
                {industries.map((ind) => {
                  const c = industryColors(ind.label);
                  return (
                    <IndustryChip
                      key={ind.key}
                      type="button"
                      $active={industryFilter === ind.key}
                      $bg={c.bg} $fg={c.fg} $border={c.border}
                      onClick={() => setIndustryFilter(industryFilter === ind.key ? null : ind.key)}
                    >
                      {ind.label} <ChipCount>{ind.count}</ChipCount>
                    </IndustryChip>
                  );
                })}
                {untaggedCount > 0 && (
                  <IndustryChip
                    type="button"
                    $active={industryFilter === '__none__'}
                    $bg="transparent" $fg="currentColor" $border="currentColor"
                    onClick={() => setIndustryFilter(industryFilter === '__none__' ? null : '__none__')}
                  >
                    Untagged <ChipCount>{untaggedCount}</ChipCount>
                  </IndustryChip>
                )}
              </IndustryFilters>
            )}

            <DimWhenIntact $dim={keepPhotoIntact}>
              {visible.length === 0 ? (
                <EmptyGrid>
                  {query.trim()
                    ? `No styles match “${query.trim()}”.`
                    : 'No styles in this industry yet.'}
                </EmptyGrid>
              ) : (
                <StyleGrid>
                  {visible.map((s) => {
                    const active = selectedStyleIds.includes(s.id);
                    return (
                      <StyleCard
                        key={s.id}
                        type="button"
                        $active={active}
                        onClick={() => toggleBakedStyle(s.id)}
                        aria-pressed={active}
                        title={s.label}
                      >
                        {s.preview
                          ? <img src={s.preview} alt="" loading="lazy" />
                          : <StylePlaceholder />}
                        <StyleName>{s.label}</StyleName>
                        {(() => {
                          const ind = normalizeIndustry(s.industry);
                          if (!ind) return null;
                          const c = industryColors(ind);
                          return <IndustryTag $bg={c.bg} $fg={c.fg} $border={c.border}>{ind}</IndustryTag>;
                        })()}
                        {active && <PickBadge><CheckGlyph /></PickBadge>}
                        {s.custom && <CustomBadge>Custom</CustomBadge>}
                      </StyleCard>
                    );
                  })}
                </StyleGrid>
              )}
            </DimWhenIntact>
          </GridPane>

          <SidePane>
            <SideTitle>Options</SideTitle>

            {anyGreenboxSelected && (
              <OptionRow>
                <input
                  type="checkbox"
                  checked={autoFillGreenbox}
                  onChange={(e) => setAutoFillGreenbox(e.target.checked)}
                />
                <span>
                  <strong>Auto-fill the green zone</strong>{' '}
                  <em>— the AI picks the best brand photo and drops it in. You can swap it later.</em>
                </span>
              </OptionRow>
            )}

            <OptionRow>
              <input type="checkbox" checked={useLogo} onChange={(e) => setUseLogo(e.target.checked)} />
              <span>
                <strong>Stamp my logo</strong>{' '}
                <em>— optional; off by default. You can also add it later in the editor.</em>
              </span>
            </OptionRow>

            {myPhotos.length > 0 && (
              <>
                <SideTitle style={{ marginTop: 18 }}>My photos</SideTitle>

                <OptionRow>
                  <input
                    type="checkbox"
                    checked={keepPhotoIntact}
                    onChange={(e) => setKeepPhotoIntact(e.target.checked)}
                  />
                  <span>
                    <strong>Keep my photo as-is</strong>{' '}
                    <em>
                      — don&apos;t restyle it. Use my exact photo as the background and just add
                      the text and logo on top. The poster styles are ignored.
                    </em>
                  </span>
                </OptionRow>

                <div style={{ fontSize: 13, color: 'inherit', marginTop: 4 }}>
                  {keepPhotoIntact
                    ? 'Pick the photo to use as the background.'
                    : 'Optional: the AI transforms these into the style you picked.'}
                </div>

                <PhotoGrid>
                  {myPhotos.map((ph) => {
                    const sel = selectedPhotoIds.includes(ph.id);
                    return (
                      <PhotoCell
                        key={ph.id}
                        type="button"
                        $active={sel}
                        onClick={() => togglePhoto(ph.id)}
                        aria-pressed={sel}
                        title={ph.name || 'photo'}
                      >
                        <img src={ph.url} alt="" loading="lazy" />
                        {sel && <PhotoCheck><CheckGlyph size={11} /></PhotoCheck>}
                      </PhotoCell>
                    );
                  })}
                </PhotoGrid>
              </>
            )}
          </SidePane>
        </Body>

        <Foot>
          <FootCount>
            {keepPhotoIntact
              ? <><strong>{selectedPhotoIds.length}</strong> photo{selectedPhotoIds.length !== 1 ? 's' : ''} selected</>
              : <><strong>{selectedStyleIds.length}</strong> style{selectedStyleIds.length !== 1 ? 's' : ''} selected</>}
          </FootCount>
          <FootActions>
            <GhostBtn type="button" onClick={onClose}>Cancel</GhostBtn>
            <PrimaryBtn
              type="button"
              disabled={!canContinue}
              onClick={() => { confirmBakedStyles(); onClose(); }}
            >
              {keepPhotoIntact
                ? (selectedPhotoIds.length > 0 ? 'Continue' : 'Pick at least one photo')
                : (selectedStyleIds.length > 0 ? 'Continue' : 'Pick at least one style')}
            </PrimaryBtn>
          </FootActions>
        </Foot>
      </Panel>
    </Overlay>,
    document.body
  );
}
